import { useState } from 'react';
import { useLocation, useNavigate, Outlet, Link } from 'react-router-dom';
import { clearAdminSession } from '@/lib/admin';
import { supabase } from '@/lib/supabase';

interface MenuItem {
  path: string;
  label: string;
  icon: string;
}

const menu: MenuItem[] = [
  { path: '/admin/dashboard', label: '首页看板', icon: 'ri-dashboard-3-line' },
  { path: '/admin/users', label: '用户管理', icon: 'ri-user-settings-line' },
  { path: '/admin/orders', label: '订单管理', icon: 'ri-file-list-3-line' },
  { path: '/admin/notices', label: '公告管理', icon: 'ri-megaphone-line' },
  { path: '/admin/deposits', label: '入金管理', icon: 'ri-currency-line' },
  { path: '/admin/channels', label: '收款通道', icon: 'ri-wallet-3-line' },
  { path: '/admin/withdrawals', label: '出金管理', icon: 'ri-bank-line' },
  { path: '/admin/pickups', label: '提货管理', icon: 'ri-truck-line' },
  { path: '/admin/schedule', label: '交易时间', icon: 'ri-time-line' },
  { path: '/admin/password', label: '修改密码', icon: 'ri-lock-2-line' },
];

const titleMap: Record<string, string> = {
  '/admin/dashboard': '首页看板',
  '/admin/users': '用户管理',
  '/admin/orders': '订单管理',
  '/admin/notices': '公告管理',
  '/admin/deposits': '入金管理',
  '/admin/channels': '收款通道',
  '/admin/withdrawals': '出金管理',
  '/admin/pickups': '提货管理',
  '/admin/schedule': '交易时间',
  '/admin/password': '修改密码',
};

function SidebarContent({
  currentPath,
  onNavigate,
}: {
  currentPath: string;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    clearAdminSession();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="flex flex-col h-full bg-brown-950 text-brown-100">
      {/* Logo */}
      <div className="h-14 flex items-center gap-2.5 px-5 border-b border-white/5">
        <div className="w-8 h-8 rounded-md bg-gold-500 flex items-center justify-center">
          <span className="w-5 h-5 flex items-center justify-center text-brown-950">
            <i className="ri-vip-crown-fill text-sm"></i>
          </span>
        </div>
        <div className="leading-tight">
          <p className="text-gold-400 text-sm font-bold tracking-tight">CJJY</p>
          <p className="text-[10px] text-brown-400 tracking-widest">管理后台</p>
        </div>
      </div>

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1 no-scrollbar">
        {menu.map((item) => {
          const active =
            currentPath === item.path || currentPath.startsWith(`${item.path}/`);
          return (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                onNavigate();
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm cursor-pointer whitespace-nowrap transition-colors ${
                active
                  ? 'bg-gold-500 text-brown-950 font-medium'
                  : 'text-brown-300 hover:bg-white/5 hover:text-brown-100'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-lg`}></i>
              </span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Back to site + logout */}
      <div className="px-3 pb-4 space-y-1 border-t border-white/5 pt-3">
        <Link
          to="/home"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-brown-300 hover:bg-white/5 hover:text-brown-100 transition-colors whitespace-nowrap"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-global-line text-lg"></i>
          </span>
          <span>返回前台</span>
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-brown-300 hover:bg-white/5 hover:text-brown-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-logout-box-r-line text-lg"></i>
          </span>
          <span>退出登录</span>
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const currentPath = location.pathname;
  const title = titleMap[currentPath] || '管理后台';

  const handleLogout = async () => {
    await supabase.auth.signOut();
    clearAdminSession();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-brown-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-60 z-30">
        <SidebarContent currentPath={currentPath} onNavigate={() => {}} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute inset-y-0 left-0 w-60 bg-brown-950 animate-slide-up">
            <div className="flex justify-end p-3">
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-brown-300 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="-mt-8">
              <SidebarContent currentPath={currentPath} onNavigate={() => setDrawerOpen(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Main area */}
      <div className="lg:ml-60 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-14 bg-white border-b border-brown-100 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden w-9 h-9 flex items-center justify-center text-brown-700 cursor-pointer"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="text-brown-400">管理后台</span>
              <span className="text-brown-300">/</span>
              <span className="text-brown-900 font-medium">{title}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/home')}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-brown-600 text-sm hover:bg-brown-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-global-line text-base"></i>
              </span>
              返回前台
            </button>
            <div className="flex items-center gap-2 pl-3 border-l border-brown-100">
              <div className="w-8 h-8 rounded-full bg-gold-500 flex items-center justify-center">
                <span className="w-5 h-5 flex items-center justify-center text-brown-950">
                  <i className="ri-admin-line text-sm"></i>
                </span>
              </div>
              <span className="text-brown-800 text-sm font-medium hidden sm:block">管理员</span>
              <button
                onClick={handleLogout}
                className="w-8 h-8 flex items-center justify-center text-brown-500 hover:text-red-500 transition-colors cursor-pointer"
                title="退出登录"
              >
                <i className="ri-logout-box-r-line text-lg"></i>
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}